import type { FC } from "react";
import { CheckIcon, CirclePlusIcon } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/ds/shadcn/components/ui/card";
import { Button } from "@/ds/shadcn/components/ui/button";
import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { cn } from "@/ds/utils";

export interface SubscriptionPlanFeature {
	readonly text: string;
	readonly inherited?: boolean;
}

export interface SubscriptionPlanCardProps {
	title: string;
	description: string;
	amount: number;
	features: SubscriptionPlanFeature[];
	discounted?: boolean;
	onSubscribe?: () => void;
}

export const SubscriptionPlanCard: FC<SubscriptionPlanCardProps> = props => {
	const {
		title,
		description,
		amount,
		features,
		discounted = false,
		onSubscribe
	} = props;

	return (
		<Card>
			<CardHeader>
				<CardTitle>{title}</CardTitle>
				<CardDescription>{description}</CardDescription>
			</CardHeader>

			<CardContent className="flex flex-1 flex-col gap-6">
				<div className="flex items-baseline gap-2">
					<MoneyDisplay
						className={cn("text-3xl font-bold", discounted && "text-primary")}
						mode="Standard"
						money={{ value: amount, currency: "USD" }} />
					<span className="text-sm text-muted-foreground">per month</span>
				</div>

				<div className="flex flex-col gap-3">
					<div className="text-sm font-medium">Features</div>
					<ul className="flex flex-col gap-2 text-sm">
						{
							features.map(x => (
								<li key={x.text} className="flex gap-2">
									{
										x.inherited
											? <CirclePlusIcon className="mt-0.5 size-4 shrink-0 text-primary" />
											: <CheckIcon className="mt-0.5 size-4 shrink-0 text-primary" />
									}
									<span>{x.text}</span>
								</li>
							))
						}
					</ul>
				</div>
			</CardContent>

			<CardFooter>
				{
					onSubscribe
						? <Button className="w-full" onClick={onSubscribe}>Subscribe</Button>
						: <Button className="w-full" variant="outline" disabled>Current Plan</Button>
				}
			</CardFooter>
		</Card>
	);
};
